import type { M2MStructure, RelationType } from '#layers/data-model/types/relation';
import type { GlobalCollectionId } from '#layers/data-model/types/schema/ids';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { CompositePrimaryKey } from '#layers/data-model/types/schema/primary-key';
import type { ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleConstraint, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { isNil } from '#layers/base/public';
import { WorkspaceCollectionIds } from '#layers/data-model/constants/schema/collection-ids';
import { ReferentialAction } from '#layers/data-model/constants/schema/schema';
import { getCollectionPrimaryKeyFields } from './schema/collection';

/**
 * Detects if a relation field is part of an M2M relation.
 *
 * M2M detection relies on `oppositeJunctionRelationFieldId` in the endpoint metas.
 * This field links to the junction's "other leg" - enabling traversal of the full M2M structure.
 */
export function isM2M(field: ResolvedMonospaceSingleRelationField): boolean {
	return !isNil(field.meta?.oppositeJunctionRelationFieldId);
}

export function sourceIsConstrained(relationType: RelationType): boolean {
	return relationType === 'm2o' || relationType === 'o2o';
}

// M2M cascades remove orphaned junction rows; new single-relation FKs are nullable.
export function defaultReferentialActions(relationType: RelationType): { onDelete: ReferentialAction; onUpdate: ReferentialAction } {
	return {
		onDelete: relationType === 'm2m' ? ReferentialAction.Cascade : ReferentialAction.SetNull,
		onUpdate: ReferentialAction.NoAction,
	};
}

export function isSelfReferential(
	sourceCollectionId: GlobalCollectionId | null,
	targetCollectionId: GlobalCollectionId | null,
): boolean {
	return !!sourceCollectionId && sourceCollectionId === targetCollectionId;
}

/**
 * Derives the junction leg that leaves towards the M2M target, for callers that do not yet have
 * `oppositeJunctionRelationFieldId` to look it up by. Once that id exists, prefer
 * {@link getM2MStructure}, which resolves the declared leg rather than inferring one.
 *
 * A junction has exactly two legs, the back reference to this endpoint and the one reaching the
 * target. Any other shape resolves to nothing rather than to a guess, since picking the wrong leg
 * would silently make the relation self-referential.
 */
export function getJunctionTargetLeg(field: ResolvedMonospaceSingleRelationField): ResolvedMonospaceSingleRelationField | undefined {
	const legs = field.oppositeCollection?.relationFields ?? [];

	if (!legs.some((leg) => leg.id === field.oppositeRelationFieldId)) return undefined;

	const targetLegs = legs.filter((leg) => leg.id !== field.oppositeRelationFieldId);

	return targetLegs.length === 1 ? targetLegs[0] : undefined;
}

/**
 * Gets the complete M2M structure from any endpoint field.
 *
 * The traversal is:
 * - field.oppositeRelationField = junction's M2O to this side
 * - field.meta.oppositeJunctionRelationFieldId, resolved against the junction's own relation fields
 *   = junction's M2O to the other side
 * - That field's oppositeRelationField = the other endpoint's O2M
 *
 * @example
 * // From Articles.authors field:
 * const structure = getM2MStructure(authorsField);
 * // structure.junction.toSource = ArticlesAuthors.article
 * // structure.junction.toTarget = ArticlesAuthors.author
 * // structure.target.field = Authors.articles
 */
export function getM2MStructure(field: ResolvedMonospaceSingleRelationField): M2MStructure {
	if (!isM2M(field)) {
		throw new Error(`Field "${field.apiName}" is not an M2M relation (missing oppositeJunctionRelationFieldId in meta)`);
	}

	const junctionToSource = field.oppositeRelationField;

	if (!junctionToSource) {
		throw new Error(`M2M field "${field.apiName}" is missing junction relation (oppositeRelationField)`);
	}

	// Falls back to the meta relation for junctions that resolve it, but a junction declared by a
	// schema override carries a plain meta object that relation can never resolve through.
	const junctionToTargetId = field.meta?.oppositeJunctionRelationFieldId;
	const junctionToTarget = field.oppositeCollection?.relationFields?.find((relationField) => relationField.id === junctionToTargetId)
		?? field.meta?.oppositeJunctionRelationFieldMeta?.field;

	if (!junctionToTarget) {
		throw new Error(`M2M field "${field.apiName}" is missing opposite junction relation meta`);
	}

	const targetField = junctionToTarget.oppositeRelationField;

	if (!targetField) {
		throw new Error(`M2M field "${field.apiName}" is missing target endpoint field`);
	}

	return {
		source: {
			field,
			collectionId: field.collectionId,
		},
		junction: {
			collectionId: junctionToSource.collectionId,
			toSource: junctionToSource,
			toTarget: junctionToTarget,
		},
		target: {
			field: targetField,
			collectionId: targetField.collectionId,
		},
	};
}

/**
 * {@link getM2MStructure} for callers that treat an unwalkable relation as "not an M2M".
 *
 * The structure is resolved from the live relation graph, so a partially resolved schema makes it
 * throw. Callers are overwhelmingly in computed properties and render paths where that would take
 * out a whole subtree rather than degrade.
 */
export function getM2MStructureSafe(field: ResolvedMonospaceSingleRelationField): M2MStructure | undefined {
	if (!isM2M(field)) return undefined;

	try {
		return getM2MStructure(field);
	}
	catch {
		return undefined;
	}
}

export function inferRelationType(relationField: ResolvedMonospaceSingleRelationField): RelationType {
	// Check for M2M first
	if (isM2M(relationField)) {
		return 'm2m';
	}

	if (relationField.isList) {
		return 'o2m';
	}
	else if (relationField.oppositeRelationField?.isList) {
		return 'm2o';
	}
	else {
		return 'o2o';
	}
}

export function isRelationToMonospaceFiles(field: ResolvedMonospaceSingleRelationField) {
	if (field.oppositeCollectionId === WorkspaceCollectionIds.MonospaceFile) return true;

	// For an m2m relation the source field points at the junction, not the file
	// collection, so walk the structure to its ultimate target.
	return getM2MStructureSafe(field)?.target.collectionId === WorkspaceCollectionIds.MonospaceFile;
}

// Matched by api name rather than a `WorkspaceCollectionIds` entry like the files check above: the
// users collection has no generated id constant yet (see the TODO in `collection-ids.ts`).
export function isRelationToMonospaceUsers(field: ResolvedMonospaceSingleRelationField) {
	if (field.oppositeCollection?.apiName === 'MonospaceSystemUser') return true;

	// For an m2m relation the source field points at the junction, not the users
	// collection, so walk the structure to its ultimate target.
	return getM2MStructureSafe(field)?.target.field.collection?.apiName === 'MonospaceSystemUser';
}

export function getRelationConstraint(relationField: ResolvedMonospaceSingleRelationField) {
	const thisConstraint = relationField.constraint;
	const thisSideConstrained = !!thisConstraint;
	const oppositeConstraint = relationField.oppositeRelationField?.constraint;
	const oppositeSideConstrained = !!oppositeConstraint;
	const constraint = thisConstraint ?? oppositeConstraint;
	const constrainedCollection = thisSideConstrained ? relationField.collection : relationField.oppositeCollection;
	const referencedCollection = !thisSideConstrained ? relationField.collection : relationField.oppositeCollection;

	return {
		thisSideConstrained,
		oppositeSideConstrained,
		constrainedCollection,
		referencedCollection,
		constraint,
	};
}

/**
 * Get the constrained and referenced fields of a constraint.
 */
export function getRelationConstraintFields(constraint: ResolvedMonospaceSingleConstraint) {
	const constrainedFields: ResolvedMonospacePrimitiveField[] = [];
	const referencedFields: ResolvedMonospacePrimitiveField[] = [];

	for (const mapping of constraint.fieldMappings) {
		if (!mapping.constrainedField || !mapping.referencedField) continue;

		constrainedFields.push(mapping.constrainedField);
		referencedFields.push(mapping.referencedField);
	}

	return { constrainedFields, referencedFields };
}

/**
 * Get the unique field set, on the referenced collection, that this relation's foreign key points
 * at. This is the reference key the relation actually matches on — usually the primary key, but a
 * relation may reference any unique index instead (a non-pk or composite reference key).
 */
export function getRelationReferencedFields(relation: ResolvedMonospaceSingleRelationField): ResolvedMonospacePrimitiveField[] {
	const { constraint } = getRelationConstraint(relation);

	if (!constraint) {
		return [];
	}

	return getRelationConstraintFields(constraint).referencedFields;
}

/**
 * Resolve the fields a picked related record should be identified by. The fields must live on the
 * opposite collection being picked, so they are the referenced fields when this side is constrained
 * and the constrained fields when the opposite side is constrained.
 */
export function getRelationReferenceKeyFields(relation: ResolvedMonospaceSingleRelationField): ResolvedMonospacePrimitiveField[] {
	const { constraint, thisSideConstrained } = getRelationConstraint(relation);

	if (constraint) {
		const { constrainedFields, referencedFields } = getRelationConstraintFields(constraint);
		const oppositeFields = thisSideConstrained ? referencedFields : constrainedFields;
		if (oppositeFields.length > 0) return oppositeFields;
	}

	return relation.oppositeCollection ? getCollectionPrimaryKeyFields(relation.oppositeCollection) : [];
}

/**
 * Checks whether an item matches a key on every field the key carries. Works for any unique field
 * set — the primary key or a relation's referenced (reference) key — by comparing the key's own
 * fields rather than assuming the primary key.
 */
export function isItemMatchingKey(item: AnyItem, key: CompositePrimaryKey): boolean {
	const fieldNames = Object.keys(key);
	if (fieldNames.length === 0) return false;

	return fieldNames.every((fieldName) => item[fieldName] === key[fieldName]);
}

/**
 * Get the fields which are backing either side of a relation field.
 *
 * The returned fields are viewed from the side of the relation passed in, `fields` being the primitive
 * fields that live on this side of the relation, `oppositeFields` living on the opposite collection.
 */
export function getRelationPrimitiveFields(relation: ResolvedMonospaceSingleRelationField): {
	fields: ResolvedMonospacePrimitiveField[];
	oppositeFields: ResolvedMonospacePrimitiveField[];
} {
	const { thisSideConstrained, constraint } = getRelationConstraint(relation);

	if (!constraint) {
		return {
			fields: [],
			oppositeFields: [],
		};
	}

	const { constrainedFields, referencedFields } = getRelationConstraintFields(constraint);

	return {
		fields: thisSideConstrained ? constrainedFields : referencedFields,
		oppositeFields: thisSideConstrained ? referencedFields : constrainedFields,
	};
}
